import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h2>
          <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.coverImage,
    url: `https://51heiliao.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '黑料聚合网',
    },
  };

  return (
    <PageLayout>
      <SEO
        title={`${article.title} - 黑料聚合网`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://51heiliao.com/article/${article.slug}`}
        ogType="article"
        jsonLd={jsonLdArticle}
      />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: category?.name || article.categorySlug, href: `/category/${article.categorySlug}` },
          { label: article.title },
        ]}
      />

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-6">
          <Link
            to={`/category/${article.categorySlug}`}
            className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full mb-4 hover:bg-primary-100 transition-colors"
          >
            {category?.name || article.categorySlug}
          </Link>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              {article.readTime} 分钟阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
            </span>
          </div>
        </div>

        <div className="mb-8 rounded-xl overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full aspect-[16/9] object-cover"
          />
        </div>

        <div
          className="prose-custom text-foreground-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-10 pt-6 border-t border-background-200/70">
          <h4 className="text-sm font-semibold text-foreground-900 mb-3">文章标签</h4>
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-foreground-600 bg-background-100 px-3 py-1.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between pt-6 border-t border-background-200/70">
          <Link
            to={`/category/${article.categorySlug}`}
            className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回 {category?.name || '分类'}
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
          >
            返回首页
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 border-t border-background-200/70">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
            相关推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
            {relatedArticles.map((rel) => (
              <ArticleCard key={rel.slug} {...rel} />
            ))}
          </div>
        </section>
      )}
    </PageLayout>
  );
}